import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { renderFramedCanvas } from '../lib/render';
import type { FrameSettings, PhotoAsset } from '../types';
import { useLanguage } from '../i18n';

export function CanvasPreview({ photo, settings }: { photo: PhotoAsset; settings: FrameSettings }): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { t } = useLanguage();
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [rendering, setRendering] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setRendering(true);

    const frame = window.requestAnimationFrame(() => {
      if (cancelled || !canvasRef.current) return;
      const preview = renderFramedCanvas(photo, settings, { maxLongEdge: 1400 });
      const canvas = canvasRef.current;
      canvas.width = preview.width;
      canvas.height = preview.height;
      const ctx = canvas.getContext('2d');
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
      ctx?.drawImage(preview, 0, 0);
      setSize({ width: preview.width, height: preview.height });
      setRendering(false);
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
    };
  }, [photo, settings]);

  return (
    <div className="flex h-full min-h-[420px] items-center justify-center bg-[color:var(--canvas)] p-4 sm:p-8">
      <div className="relative flex max-h-full max-w-full items-center justify-center">
        <canvas
          ref={canvasRef}
          className="max-h-[calc(100vh-9rem)] max-w-full rounded-md shadow-[0_24px_80px_rgb(15_23_42_/_0.14)]"
          aria-label={t('preview.label', { file: photo.fileName })}
        />
        {rendering ? (
          <div className="absolute inset-0 flex items-center justify-center rounded-md bg-[color:var(--surface)]/40">
            <Loader2 className="animate-spin text-[color:var(--muted)]" size={28} />
          </div>
        ) : null}
        <div className="absolute bottom-3 right-3 rounded bg-[color:var(--panel)]/88 px-2 py-1 text-xs font-medium text-[color:var(--muted)] shadow-sm">
          {size.width} x {size.height}
        </div>
      </div>
    </div>
  );
}
